// Phase H.10 — HTML round-trip helpers for the TipTap template editor.
//
// `pretagsToChips` converts plain `{{tag.path}}` text in the raw template
// HTML into `<span data-merge-tag="path">{{path}}</span>` spans so TipTap's
// MergeTagNode `parseHTML` rule can pick them up. Only converts standalone
// runs that aren't already inside a merge-tag span — idempotent.
//
// `chipsToPlainTags` is the post-save normalizer that strips the chip span
// wrappers back to plain `{{tag.path}}` text. This guarantees the saved
// HTML is byte-for-byte the same shape the server-side PDF renderer has
// been consuming for the lifetime of the project — no PDF pipeline changes
// required.

const TAG_INNER = "[A-Za-z0-9_.]+";

// Match `{{path}}` but only outside of HTML tags. We do this by splitting on
// tag boundaries.
export function pretagsToChips(html) {
    if (!html) return "";
    const parts = [];
    const re = /<[^>]+>/g; // tags
    let last = 0;
    let m;
    while ((m = re.exec(html)) !== null) {
        // Text segment before this tag
        const text = html.slice(last, m.index);
        parts.push(_wrapStandaloneTags(text));
        parts.push(m[0]);
        last = re.lastIndex;
    }
    parts.push(_wrapStandaloneTags(html.slice(last)));
    return parts.join("");
}

function _wrapStandaloneTags(text) {
    if (!text) return "";
    return text.replace(
        new RegExp(`\\{\\{\\s*(${TAG_INNER})\\s*\\}\\}`, "g"),
        (_, path) =>
            `<span data-merge-tag="${path}">{{${path}}}</span>`
    );
}

// Strip <span data-merge-tag="X">…</span> down to just `{{X}}`. The span's
// inner text may contain the literal `{{X}}` already or, in some edge cases
// (paste from elsewhere), bare path text; we always emit canonical
// `{{path}}` based on the data attribute.
export function chipsToPlainTags(html) {
    if (!html) return "";
    return html.replace(
        /<span[^>]*data-merge-tag="([^"]+)"[^>]*>[^<]*<\/span>/g,
        (_, path) => `{{${path}}}`
    );
}

// Tidy a few TipTap-isms we don't want shipping to the PDF renderer:
//  - Empty `style=""` attributes (TipTap sometimes leaves these when a mark
//    is removed mid-edit).
//  - `<p></p>` empty paragraphs collapse to a single trailing `<p></p>`.
//  - Trailing whitespace.
export function tidyForSave(html) {
    if (!html) return "";
    let out = html;
    out = out.replace(/\s+style=""/g, "");
    out = out.replace(/<p><\/p>\s*$/g, "");
    out = out.trim();
    return out;
}

// Convenience: run pretagsToChips on incoming raw template HTML so TipTap
// can parse merge tags as nodes.
export function loadHtmlForEditor(raw) {
    return pretagsToChips(raw || "");
}

// Convenience: run chipsToPlainTags + tidyForSave on the editor's getHTML()
// output before persisting.
export function saveHtmlFromEditor(html) {
    return tidyForSave(chipsToPlainTags(html || ""));
}
